import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Select,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect } from "react";
import Grid2 from "@mui/material/Grid2";
import TextInput from "../../components/Input/TextInput";
import CountrySelect from "../../components/SelectComponent/SelectCountry";
import { MuiPhoneComponent } from "../../components/Input/MuiPhoneComponent";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { Form } from "formik";
import TextAreaComponent from "../../components/Input/TextAreaComponent";
import Checkbox from "@mui/material/Checkbox";
import DatePickerComponent from "../../components/SelectComponent/DatePicker";
import TimePickerComponent from "../../components/SelectComponent/TimePickerComponent";
import CheckboxListComponent from "../../components/SelectComponent/CheckBoxListComponent";
import RadioComponent from "../../components/SelectComponent/RadioComponent";
import NumberInputComponent from "../../components/Input/NumberInput";
import CardSection from "./CardSection";

// ── Inline label (left, md:3) + input (right, md:9) ─────────────────────────
const InlineField = ({ label, required, css, htmlFor, children }) => (
  <Grid2 size={{ xs: 12 }}>
    <Grid2 container spacing={2} alignItems="center">
      {/* Label column */}
      <Grid2 size={{ md: 3, xs: 12 }}>
        <Typography
          component="label"
          htmlFor={htmlFor}
          sx={{
            display: "block",
            fontWeight: "bold",
            fontSize: "14px",
            color: css?.labelColor,
            textAlign: "left",
          }}
        >
          {label}
          {required && (
            <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>
              *
            </Box>
          )}
        </Typography>
      </Grid2>

      {/* Input column */}
      <Grid2 size={{ md: 9, xs: 12 }}>
        {children}
      </Grid2>
    </Grid2>
  </Grid2>
);

// ── Two-column guest row: each column = [label (md:3) | input (md:3)] ────────
// Full row = 12 cols → left pair = 6, right pair = 6
const GuestRow = ({ pairs, css }) => (
  <Grid2 container spacing={2} sx={{ mb: 2 }} alignItems="center">
    {pairs.map(({ fieldName, isRequired, renderInput }, j) => (
      <Grid2 size={{ md: 6, xs: 12 }} key={j}>
        <Grid2 container spacing={1} alignItems="center">
          {/* Label */}
          <Grid2 size={{ md: 4, xs: 12 }}>
            <Typography
              component="label"
              htmlFor={fieldName}
              sx={{
                display: "block",
                fontWeight: "bold",
                fontSize: "13px",
                color: css?.labelColor,
                textAlign: "left",
                whiteSpace: "nowrap",
              }}
            >
              {fieldName}
              {isRequired && (
                <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>
                  *
                </Box>
              )}
            </Typography>
          </Grid2>
          {/* Input */}
          <Grid2 size={{ md: 8, xs: 12 }}>
            {renderInput()}
          </Grid2>
        </Grid2>
      </Grid2>
    ))}
  </Grid2>
);

// ────────────────────────────────────────────────────────────────────────────

const UserDetailsSection = ({
  price, steps,
  activeStep,
  handleBack,
  handleNextFunc,
  isLoading,
  languageId,
  defaultLanguage,
  values,
  errors,
  touched,
  setField,
  totalCount,
  languageWords,
  reservType,
  selectedDate,
  adult,
  child,
  kid,
  baby,
  toddler,
  selectedExtraProduct,
  time,
  transactionDetails,
  selectedCombination,
  transactionPrice,
  excludingVat,
  totalVat,
  totalIncludingVAT,
  totalCostYourBooking,
  GuestDetailsData,
  extraFieldsData,
  setAddFields,
  setGuestFields,
  combinationUpsale,
  total_product_price,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { widgetConfig, guestInfo, css } = useSelector(
    (store) => store?.widgetSettings
  );
  const Css = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Contact details"
  )?.fields;

  useEffect(() => {
    const guestFields = GuestDetailsData?.map((field) => ({
      idfield: field?.tag,
      data: values[
        field?.guestLanguages?.find((item) => item?.fieldname == "name")?.data
      ],
    }));
    const updatedFields = extraFieldsData?.map((field) => {
      let fieldValue =
        values[field?.fieldsLanguages.find((f) => f?.fieldname === "name")?.data];
      if (field.type === "time" && fieldValue) {
        fieldValue = dayjs(fieldValue)?.format("HH:mm:ss");
      }
      return {
        idfields: field?.idfields,
        name: field.fieldsLanguages.find((f) => f?.fieldname === "name")?.data,
        data: fieldValue,
      };
    });
    dispatch(setAddFields(updatedFields));
    dispatch(setGuestFields(guestFields));
  }, [values]);

  return (
    <Form>
      <Box sx={{ mt: 5, p: 2 }}>
        <Grid2 container spacing={2}>

          {/* ════ LEFT: form area ════════════════════════════════════════ */}
          <Grid2 size={{ md: 9, sm: 12, xs: 12 }}>

            {/* Heading */}
            <Typography
              variant="h6"
              sx={{
                color: Css?.contactdetailsHeadingClr,
                fontSize: "20px",
                fontWeight: "bold",
                mt: 1,
                mb: 3,
              }}
            >
              {languageWords["ContactData"]}
            </Typography>

            {/* ── Read-only (logged-in token) ── */}
            {widgetConfig?.token ? (
              <Box>
                {[
                  { key: "salutation",  label: languageWords?.Salutation  || "Salutation" },
                  { key: "firstname",   label: languageWords?.firstname   || "First Name" },
                  { key: "lastname",    label: languageWords?.lastname    || "Last Name" },
                  { key: "email",       label: languageWords?.email       || "Email" },
                  { key: "address",     label: languageWords?.address     || "Address" },
                  { key: "country",     label: languageWords?.country     || "Country" },
                  { key: "zipcode",     label: languageWords?.zipcode     || "Zipcode" },
                  { key: "phone",       label: languageWords?.phone       || "Phone" },
                  { key: "place",       label: languageWords?.place       || "Place" },
                  { key: "otherfields", label: languageWords?.otherfields || "Other fields" },
                ]
                  .filter(({ key }) => guestInfo?.[key])
                  .reduce((rows, item, idx, arr) => {
                    if (idx % 2 === 0) rows.push([item, arr[idx + 1]].filter(Boolean));
                    return rows;
                  }, [])
                  .map((pair, rowIdx) => (
                    <Grid2 container key={rowIdx} spacing={2} sx={{ mb: 2 }}>
                      {pair.map(({ key, label }) => (
                        <Grid2 size={{ md: 6, xs: 12 }} key={key}>
                          <Grid2 container spacing={1} alignItems="center">
                            <Grid2 size={{ md: 3, xs: 12 }}>
                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  color: Css?.labelColor,
                                  fontSize: "13px",
                                  textAlign: "left",
                                }}
                              >
                                {label}
                              </Typography>
                            </Grid2>
                            <Grid2 size={{ md: 8, xs: 12 }}>
                              <Typography sx={{ color: Css?.labelColor }}>
                                {guestInfo[key]}
                              </Typography>
                            </Grid2>
                          </Grid2>
                        </Grid2>
                      ))}
                    </Grid2>
                  ))}
              </Box>
            ) : (
             
              (() => {
                const fields = GuestDetailsData?.map((field, i) => {
                  const fieldName = field?.guestLanguages?.find(
                    (item) => item?.fieldname === "name"
                  )?.data;
                  const isRequired = field?.required || field?.isrequired;

                  const renderInput = () => {
                    switch (field?.tag) {
                      case "{salutation}":
                        return (
                          <FormControl fullWidth>
                            <RadioGroup
                              row
                              name={fieldName}
                              value={values[fieldName]}
                              onChange={(e) => setField(fieldName, e.target.value)}
                            >
                              {field?.guestLanguages[1]?.data
                                ?.replace(/\\"/g, "")
                                ?.split("\n")
                                ?.map((item) => item.trim().replace(/^"|"$/g, ""))
                                ?.filter(Boolean)
                                ?.map((item) => (
                                  <FormControlLabel
                                    key={item} value={item}
                                    control={
                                      <Radio size="small"
                                        sx={{ "&.Mui-checked": { color: theme?.palette?.themeColor } }}
                                      />
                                    }
                                    label={item}
                                  />
                                ))}
                            </RadioGroup>
                          </FormControl>
                        );
                      case "{land}":
                        return (
                          <CountrySelect
                            style={{ width: "100%" }}
                            label={fieldName} name={fieldName}
                            value={values[fieldName]} onChange={setField}
                          />
                        );
                      case "date":
                        return <DatePickerComponent label={field?.info} name={fieldName} />;
                      case "{email}":
                        return (
                          <TextInput
                            showLabel={false}
                            label={fieldName} name={fieldName}
                            value={values[fieldName]} onChange={setField}
                            error={errors[fieldName]}
                            helperText={touched[fieldName] && errors[fieldName]}
                            borderClr={Css?.fieldBorderClr} errorBorderClr={Css?.errorBorder}
                          />
                        );
                      case "{phone}":
                        return (
                          <MuiPhoneComponent
                            showLabel={false}
                            label={fieldName} name={fieldName}
                            value={values[fieldName]} onChange={setField}
                            error={errors[fieldName]}
                            helperText={touched[fieldName] && errors[fieldName]}
                          />
                        );
                      default:
                        return (
                          <TextInput
                            showLabel={false}
                            label={fieldName} name={fieldName}
                            value={values[fieldName]} onChange={setField}
                            error={errors[fieldName]}
                            helperText={touched[fieldName] && errors[fieldName]}
                            borderClr={Css?.fieldBorderClr} errorBorderClr={Css?.errorBorder}
                          />
                        );
                    }
                  };

                  return { fieldName, isRequired, renderInput };
                }) ?? [];

                // Pair up: 2 [label+input] pairs per row
                const rows = [];
                for (let i = 0; i < fields.length; i += 2) {
                  rows.push(
                    <GuestRow
                      key={i}
                      pairs={fields.slice(i, i + 2)}
                      css={Css}
                    />
                  );
                }
                return <Box>{rows}</Box>;
              })()
            )}

            {extraFieldsData?.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: Css?.contactdetailsHeadingClr,
                    fontSize: "20px",
                    fontWeight: "bold",
                    mb: 3,
                  }}
                >
                  {languageWords["ExtraInformation"]}
                </Typography>

                <Grid2 container rowSpacing={2}>
                  {extraFieldsData?.map((field, i) => {
                    const fieldName =
                      field?.fieldsLanguages?.find((item) => item?.fieldname === "name")?.data
                      ?? field?.name;
                    const fieldInfo =
                      field?.fieldsLanguages?.find((item) => item?.fieldname === "info")?.data
                      ?? field?.info;
                    const isRequired = field?.required || field?.isrequired;

                    switch (field?.type) {

                      case "textarea":
                        return (
                          <InlineField
                            key={i}
                            label={fieldName}
                            required={isRequired}
                            css={Css}
                            showlabel={field?.showlabel}
                          >
                            <TextAreaComponent
                              label={fieldName}
                              name={fieldName?.replace(".", "")}
                              setField={setField}
                              value={values[fieldName]}
                              error={errors[fieldName]}
                            />
                          </InlineField>
                        );

                      case "number":
                        return (
                          <InlineField
                            key={i}
                            label={fieldInfo}
                            required={isRequired}
                            css={Css}
                            htmlFor={fieldName}
                          >
                            <NumberInputComponent
                              showLabel={false}
                              label={fieldInfo} name={fieldName}
                              setField={setField}
                              value={values[fieldName] || 0}
                              error={errors[fieldName]}
                            />
                          </InlineField>
                        );

                      case "date":
                        return (
                          <InlineField
                            key={i}
                            label={fieldName}
                            required={isRequired}
                            css={Css}
                            htmlFor={fieldName}
                          >
                            <DatePickerComponent
                              showLabel={false}
                              label={fieldName} name={fieldName}
                              value={values[fieldName]} onChange={setField}
                              lang={defaultLanguage} error={errors[fieldName]}
                            />
                          </InlineField>
                        );

                      case "time":
                        return (
                          <InlineField
                            key={i}
                            label={fieldName}
                            required={isRequired}
                            css={Css}
                            htmlFor={fieldName}
                          >
                            <TimePickerComponent
                              showLabel={false}
                              label={fieldName} name={fieldName}
                              value={values[fieldName]} onChange={setField}
                            />
                          </InlineField>
                        );

                      case "tel":
                        return (
                          <InlineField
                            key={i}
                            label={fieldName}
                            required={isRequired}
                            css={Css}
                          >
                            <MuiPhoneComponent
                              showLabel={false}
                              label={fieldName} name={fieldName}
                              value={values[fieldName]} onChange={setField}
                              error={errors[fieldName]}
                              helperText={touched[fieldName] && errors[fieldName]}
                            />
                          </InlineField>
                        );

                      case "radio":
                        return (
                          <InlineField
                            key={i}
                            label={field?.fieldsLanguages[0]?.data}
                            required={isRequired}
                            css={Css}
                          >
                            <RadioComponent
                              showLabel={false}
                              field={field} setField={setField}
                              error={errors[fieldName]}
                            />
                          </InlineField>
                        );

                      case "select":
                        return (
                          <InlineField
                          key={i}
                          label={fieldName}
                          required={isRequired}
                          css={Css}
                        >
                          <FormControl fullWidth size="small"> 
                            <Select
                              native
                              name={fieldName}
                              value={values[fieldName] || ""}
                              onChange={(e) => setField(fieldName, e.target.value)}
                              sx={{
                                height: "40px",          
                                borderRadius: "8px",
                                backgroundColor: "#FDFDFD",
                                fontSize: { xs: "12px", sm: "13px", md: "14px" },
                                "& select": {
                                  padding: "0 8px",     
                                  height: "40px",
                                  display: "flex",
                                  alignItems: "center",
                                },
                                "& fieldset": {
                                  borderColor: Css?.fieldBorderClr || "#EBEBEB",
                                },
                                "&:hover fieldset": {
                                  borderColor: Css?.fieldBorderClr || "#EBEBEB",
                                },
                                "&.Mui-focused fieldset": {
                                  borderColor: Css?.fieldBorderClr || "#EBEBEB",
                                },
                              }}
                            >
                              {field?.fieldsLanguages?.length > 0
                                ? field?.fieldsLanguages[2]?.data?.split("\n")?.map((item) => (
                                    <option value={item} key={item}>
                                      {item?.replace("[C]", " ")}
                                    </option>
                                  ))
                                : field?.options?.split("\n")?.map((item) => (
                                    <option value={item} key={item}>
                                      {item?.replace("[C]", " ")}
                                    </option>
                                  ))}
                            </Select>
                          </FormControl>
                        </InlineField>
                        );

                      case "termsandcondition":
                        return (
                          <Grid2 size={{ xs: 12 }} key={i}>
                            <Grid2 container spacing={2} alignItems="center">
                              <Grid2 size={{ md: 3, xs: 12 }}>
                                {fieldName && (
                                  <Typography
                                    sx={{
                                      fontWeight: "bold",
                                      fontSize: "13px",
                                      color: Css?.labelColor,
                                      textAlign: "left",
                                    }}
                                  >
                                    {fieldName}
                                    {isRequired && (
                                      <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                                    )}
                                  </Typography>
                                )}
                              </Grid2>
                              <Grid2 size={{ md: 9, xs: 12 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={values[fieldName] || false}
                                      onChange={(e) => setField(fieldName, e.target.checked)}
                                      sx={{ "&.Mui-checked": { color: theme?.details?.formCheckBoxColor } }}
                                    />
                                  }
                                  label={
                                    <a href={field?.options} target="_blank" rel="noopener noreferrer"
                                      style={{ color: theme?.details?.formLinkColor }}>
                                      {fieldInfo}
                                    </a>
                                  }
                                />
                              </Grid2>
                            </Grid2>
                          </Grid2>
                        );

                      case "checkbox":
                        return (
                          <Grid2 size={{ xs: 12 }} key={i}>
                            <Grid2 container spacing={2} alignItems="center">
                              <Grid2 size={{ md: 3, xs: 12 }} />
                              <Grid2 size={{ md: 9, xs: 12 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small" name={fieldName}
                                      checked={values[fieldName] || false}
                                      onChange={(e) => setField(fieldName, e.target.checked)}
                                      sx={{ "&.Mui-checked": { color: theme?.details?.formCheckBoxColor } }}
                                    />
                                  }
                                  label={
                                    <>
                                      {fieldInfo}
                                      {isRequired && (
                                        <Box component="span" sx={{ color: "error.main", ml: 0.3 }}>*</Box>
                                      )}
                                    </>
                                  }
                                />
                              </Grid2>
                            </Grid2>
                          </Grid2>
                        );

                      case "file":
                      case "checkboxList":
                        return (
                          <InlineField
                            key={i}
                            label={field?.fieldsLanguages[0]?.data}
                            required={isRequired}
                            css={Css}
                          >
                            <CheckboxListComponent
                              showLabel={false}
                              field={field} setField={setField}
                            />
                          </InlineField>
                        );

                      case "link":
                        return (
                          <Grid2 size={{ xs: 12 }} key={i}>
                            <Grid2 container spacing={2}>
                              <Grid2 size={{ md: 3, xs: 0 }} />
                              <Grid2 size={{ md: 9, xs: 12 }}>
                                <a
                                  href={field?.fieldsLanguages[0]?.data}
                                  target="_blank" rel="noopener noreferrer"
                                  style={{ color: theme?.details?.formLinkColor, fontSize: "15px", fontWeight: "bold" }}
                                >
                                  {field?.fieldsLanguages?.find((item) => item?.fieldname === "options")?.data}
                                </a>
                              </Grid2>
                            </Grid2>
                          </Grid2>
                        );

                      case "label":
                        return (
                          <Grid2 size={{ xs: 12 }} key={i}>
                            <Grid2 container spacing={2} alignItems="center">
                              {field?.showlabel && (
                                <Grid2 size={{ md: 3, xs: 12 }}>
                                  <Typography
                                    sx={{
                                      fontWeight: "bold",
                                      fontSize: "13px",
                                      color: Css?.labelColor,
                                      textAlign: "left",
                                    }}
                                  >
                                    {fieldName}
                                  </Typography>
                                </Grid2>
                              )}
                              <Grid2 size={field?.showlabel ? { md: 9, xs: 12 } : { xs: 12 }}>
                                <Typography>{fieldInfo}</Typography>
                              </Grid2>
                            </Grid2>
                          </Grid2>
                        );

                      default:
                        return (
                          <InlineField
                            key={i}
                            label={fieldName}
                            required={isRequired}
                            css={Css}
                            htmlFor={fieldName}
                          >
                            <TextInput
                              showLabel={false}
                              label={fieldName} name={fieldName}
                              value={values[fieldName]} placeholder={fieldInfo}
                              onChange={setField} error={errors[fieldName]}
                              helperText={touched[fieldName] && errors[fieldName]}
                              borderClr={Css?.fieldBorderClr} errorBorderClr={Css?.errorBorder}
                            />
                          </InlineField>
                        );
                    }
                  })}
                </Grid2>
              </Box>
            )}
          </Grid2>

          {/* ════ RIGHT: booking summary card ══════════════════════════ */}
          <Grid2 size={{ md: 3, sm: 12, xs: 12 }}>
            <CardSection
            touched={touched}
            values={values}
              price={price}
              totalCount={totalCount}
              languageWords={languageWords}
              reservType={reservType}
              selectedDate={selectedDate}
              adult={adult} child={child} kid={kid} baby={baby} toddler={toddler}
              selectedExtraProduct={selectedExtraProduct}
              time={time}
              transactionDetails={transactionDetails}
              selectedCombination={selectedCombination}
              transactionPrice={transactionPrice}
              excludingVat={excludingVat}
              totalVat={totalVat}
              totalIncludingVAT={totalIncludingVAT}
              totalCostYourBooking={totalCostYourBooking}
              combinationUpsale={combinationUpsale}
              total_product_price={total_product_price}
              languageId={languageId}
              steps={steps}
              activeStep={activeStep}
              handleBack={handleBack}
              handleNextFunc={handleNextFunc}
              errors={errors}
              isLoading={isLoading}
            />
          </Grid2>

        </Grid2>
      </Box>
    </Form>
  );
};

export default UserDetailsSection;



